import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/layout/Layout';
import HomePage from './components/layout/HomePage';
import Gallery from './components/gallery/Gallery';
import ProductDetail from './components/product/ProductDetail';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import ManageArtworks from './components/admin/ManageArtworks';
import EditArtwork from './components/admin/EditArtwork';
import ImageUploader from './components/admin/ImageUploader';
import Settings from './components/admin/Settings';
import ManageOrders from './components/admin/ManageOrders';
import PrintifyAdmin from './components/admin/PrintifyAdmin';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import AdminLogin from './components/auth/AdminLogin';
import Account from './components/user/Account';
import Contact from './components/contact/Contact';
import About from './components/about/About';
import Blog from './components/blog/Blog';
import BlogPost from './components/blog/BlogPost';
import Cart from './components/cart/Cart';
import PrintifyStore from './components/store/PrintifyStore';
import PrintifyProductDetail from './components/store/PrintifyProductDetail.tsx';
import PrintifyCheckout from './components/store/PrintifyCheckout';
import CheckoutSuccess from './components/store/CheckoutSuccess';
import { SettingsProvider } from './context/SettingsContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import './config/react-scheduler-config';
import { serverService } from './config/api';
import './App.css';

function App() {
  const [apiStatus, setApiStatus] = useState({ connected: false, checking: true });

  // Check backend connection on app load
  useEffect(() => {
    const checkBackendConnection = async () => {
      try {
        const response = await serverService.get('/health');
        if (response && response.status === 'UP') {
          setApiStatus({ connected: true, checking: false });
        } else {
          setApiStatus({ connected: false, checking: false });
        }
      } catch (error) {
        console.error('Backend connection error:', error);
        setApiStatus({ connected: false, checking: false });
      }
    };

    checkBackendConnection();
  }, []);

  return (
    <ThemeProvider>
      <SettingsProvider>
        <AuthProvider>
          <CartProvider>
            <Router>
              {!apiStatus.connected && !apiStatus.checking && (
                <div style={{
                  padding: '10px',
                  backgroundColor: '#fff8e1',
                  color: '#ff6d00',
                  textAlign: 'center',
                  borderBottom: '1px solid #ffcc80'
                }}>
                  Backend API connection failed. Make sure your backend server 
                </div>
              )}
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={
                  <Layout>
                    <HomePage />
                  </Layout>
                } />
                
                {/* Login and Register Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/adminlogin" element={<AdminLogin />} />
                
                <Route path="/gallery" element={
                  <Layout>
                    <Gallery />
                  </Layout>
                } />
                
                <Route path="/product/:id" element={
                  <Layout>
                    <ProductDetail mode="store" />
                  </Layout>
                } />

                <Route path="/gallery/artwork/:id" element={
                  <Layout>
                    <ProductDetail mode="gallery" />
                  </Layout>
                } />

                <Route path="/store/artwork/:id" element={
                  <Layout>
                    <ProductDetail mode="store" />
                  </Layout>
                } />
                
                {/* User Account Route */}
                <Route path="/account" element={
                  <Layout>
                    <Account />
                  </Layout>
                } />
                
                {/* About Route */}
                <Route path="/about" element={
                  <Layout>
                    <About />
                  </Layout>
                } />
                
                {/* Contact Route */}
                <Route path="/contact" element={
                  <Layout>
                    <Contact />
                  </Layout>
                } />
                
                {/* Cart Route */}
                <Route path="/cart" element={
                  <Layout>
                    <Cart />
                  </Layout>
                } />
                
                {/* Printify Store Routes */}
                <Route path="/store" element={
                  <Layout>
                    <PrintifyStore />
                  </Layout>
                } />
                
                <Route path="/store/product/:shopId/:productId" element={
                  <Layout>
                    <PrintifyProductDetail />
                  </Layout>
                } />
                
                <Route path="/checkout" element={
                  <Layout>
                    <PrintifyCheckout />
                  </Layout>
                } />
                
                <Route path="/checkout/success" element={
                  <Layout>
                    <CheckoutSuccess />
                  </Layout>
                } />
                
                {/* Blog Routes */}
                <Route path="/blog" element={
                  <Layout>
                    <Blog />
                  </Layout>
                } />
                
                <Route path="/blog/:id" element={
                  <Layout>
                    <BlogPost />
                  </Layout>
                } />
                
                {/* Admin Routes - Protected with ProtectedRoute component */}
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/artworks" element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <ManageArtworks />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/images" element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <ImageUploader />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/orders" element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <ManageOrders />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/printify" element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <PrintifyAdmin />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/settings" element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <Settings />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/artworks/edit/:id" element={
                  <ProtectedRoute>
                    <AdminLayout>
                      <EditArtwork />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                
                {/* Add more routes as needed */}
              </Routes>
            </Router>
          </CartProvider>
        </AuthProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;
